import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from '@/lib/utils';
import { Thought } from '@/services/supabase/thoughts';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Calendar, Edit, Heart, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { ThoughtDetailDialog } from './thought-detail-dialog';

interface ThoughtCardProps {
  thought: Thought;
  isSelected: boolean;
  onEdit: (thought: Thought) => void;
  onDelete: (thought: Thought) => void;
  onToggleFavorite: (thought: Thought) => void;
  className?: string;
}

export const ThoughtCard: React.FC<ThoughtCardProps> = ({
  thought,
  isSelected,
  onEdit,
  onDelete,
  onToggleFavorite,
  className
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Format the date for display in the card
  const formattedDate = React.useMemo(() => {
    try {
      const date = new Date(thought.created_at);
      return format(date, 'd MMM yyyy');
    } catch (e) {
      return '';
    }
  }, [thought.created_at]);

  const handleCardClick = () => {
    setIsDialogOpen(true);
  };

  return (
    <>
      <motion.div
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        className={cn(
          "group relative h-full flex flex-col rounded-xl border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md cursor-pointer",
          isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:border-primary/50",
          className
        )}
        onClick={handleCardClick}
      >
        {/* Favorite Indicator (Cleaner) */}
        {thought.is_favorite && (
          <div className="absolute top-3 right-3 z-10">
            <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center backdrop-blur-sm">
              <Heart className="h-3.5 w-3.5 text-primary fill-current" />
            </div>
          </div>
        )}

        <div className="p-6 flex flex-col h-full"> {/* Increased padding from p-5 to p-6 */}
          {/* Header */}
          <div className="mb-4 pr-6"> {/* Increased margin from mb-3 to mb-4 */}
            <h3 className={cn(
              "font-semibold text-lg leading-tight tracking-tight text-foreground transition-colors group-hover:text-primary", // Increased text size from text-base to text-lg
              !thought.title && "text-muted-foreground italic"
            )}>
              {thought.title || 'Untitled Thought'}
            </h3>
          </div>

          {/* Content */}
          <div className="flex-grow relative mb-5"> {/* Increased margin from mb-4 to mb-5 */}
            <p className="text-base text-muted-foreground line-clamp-4 leading-relaxed"> {/* Increased text size from text-sm to text-base */}
              {thought.content || 'Start writing your thought here...'}
            </p>
            {/* Gradient overlay for text overflow */}
            {(thought.content && thought.content.length > 100) && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
            )}
          </div>

          {/* Footer */}
          <div className="mt-auto pt-3 border-t border-border/50 flex items-center justify-between">
            <div className="flex items-center text-xs text-muted-foreground/80 font-medium">
              <Calendar className="h-3 w-3 mr-1.5 opacity-70" />
              {formattedDate}
            </div>

            {/* Actions (Visible on hover or touch) */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ButtonAction
                      icon={Edit}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(thought);
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ButtonAction
                      icon={Heart}
                      isActive={thought.is_favorite}
                      activeClass="text-primary fill-primary bg-primary/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(thought);
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Favorite</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ButtonAction
                      icon={Trash}
                      hoverClass="hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(thought);
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </motion.div>

      <ThoughtDetailDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        thought={thought}
        onEdit={onEdit}
        onToggleFavorite={onToggleFavorite}
      />
    </>
  );
};

// Helper for action buttons
const ButtonAction = ({
  icon: Icon,
  onClick,
  isActive,
  activeClass,
  hoverClass
}: {
  icon: any,
  onClick: (e: React.MouseEvent) => void,
  isActive?: boolean,
  activeClass?: string,
  hoverClass?: string
}) => (
  <button
    className={cn(
      "h-7 w-7 flex items-center justify-center rounded-md transition-colors",
      isActive ? (activeClass || "text-primary") : "text-muted-foreground hover:bg-muted",
      !isActive && hoverClass
    )}
    onClick={onClick}
  >
    <Icon className={cn("h-3.5 w-3.5", isActive && "fill-current")} />
  </button>
);
